import React from 'react';
import { useTranslation } from 'react-i18next';
import { Volume2, VolumeX } from 'lucide-react';
import type { AppLocale } from '../../i18n';
import { localeDirection } from '../../i18n';
import { useAppLocale } from '../../providers/LocaleProvider';

interface StickyAudioBlockToggleProps {
  enabled: boolean;
  onToggle: () => void;
  /** Hide when this block has no matching sticky-audio phrases. */
  visible?: boolean;
  className?: string;
  dataTourId?: string;
  /** Override UI strings and direction (admin preview EN/FA toggle). */
  locale?: AppLocale;
}

export const StickyAudioBlockToggle: React.FC<StickyAudioBlockToggleProps> = ({
  enabled,
  onToggle,
  visible = true,
  className = '',
  dataTourId,
  locale,
}) => {
  const { locale: appLocale, direction: appDirection } = useAppLocale();
  const effectiveLocale = locale ?? appLocale;
  const direction = locale != null ? localeDirection(locale) : appDirection;
  const isRtl = direction === 'rtl';
  const { t } = useTranslation('learning', { lng: effectiveLocale });

  if (!visible) return null;

  const enabledHint = t('stickyAudio.hint');
  const disabledHint = t('stickyAudio.hintDisabled');
  const label = t('stickyAudio.label');
  const hint = enabled ? enabledHint : disabledHint;

  return (
    <div
      data-tour-id={dataTourId}
      className={`sticky-audio-block-toggle inline-flex max-w-full items-center gap-0 rounded-full shadow-sm ${
        enabled ? 'is-enabled' : 'is-disabled'
      } ${isRtl ? 'sticky-audio-block-toggle--rtl flex-row-reverse' : ''} ${className}`}
      title={hint}
      dir="ltr"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={enabled}
        aria-label={enabled ? t('stickyAudio.ariaHide') : t('stickyAudio.ariaShow')}
        aria-expanded={enabled}
        dir={direction}
        className="sticky-audio-block-toggle__button inline-flex items-center rounded-full text-xs font-medium"
      >
        {enabled ? (
          <Volume2 className="sticky-audio-block-toggle__icon h-3.5 w-3.5" aria-hidden />
        ) : (
          <VolumeX className="sticky-audio-block-toggle__icon h-3.5 w-3.5" aria-hidden />
        )}
        <span className="sticky-audio-block-toggle__label">{label}</span>
      </button>
      <div className="sticky-audio-block-toggle__hint-viewport">
        <span dir={direction} className="sticky-audio-block-toggle__hint">
          <span className="sticky-audio-block-toggle__hint-text">{hint}</span>
        </span>
      </div>
    </div>
  );
};
